import { IconEye, IconEyeOff, IconCheck, IconX } from "@tabler/icons-react";
import React, { useState, useEffect, useRef } from "react";
import { UseFormRegister, FieldError, RegisterOptions } from "react-hook-form";
import FormErrorMessage from "./common/FormErrorMessage";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";
import { useLanguage } from "../context/LanguageContext";

interface InputProps {
  name: string;
  type?: string;
  title?: string;
  placeholder?: string;
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: FieldError | any;
  onChange?: (e: any) => void;
  other?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  defaultValue?: string;
  value?: string | number;
  removeDiv?: boolean;
  step?: any;
  inputMode?: any;
  inputClassName?: any;
  min?: any;
  max?: any;
  maxLength?: number;
  autoComplete?: string;
  showCounter?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  showPasswordStrength?: boolean;
}

const NewinputField: React.FC<InputProps> = ({
  name,
  type = "text",
  title,
  placeholder,
  register,
  validation,
  error,
  onChange,
  other,
  className = "",
  labelClass = "",
  disabled = false,
  defaultValue = "",
  value,
  removeDiv = false,
  step,
  inputMode,
  inputClassName,
  min,
  max,
  maxLength,
  autoComplete,
  showCounter = false,
  prefix,
  suffix,
  showPasswordStrength,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    value !== undefined && value !== null ? String(value) : defaultValue ? String(defaultValue) : ""
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useLanguage();

  let effectiveMaxLength: number | undefined = maxLength;
  if (effectiveMaxLength === undefined && validation?.maxLength) {
    if (typeof validation.maxLength === "number") {
      effectiveMaxLength = validation.maxLength;
    } else if (typeof validation.maxLength === "object" && (validation.maxLength as any).value) {
      effectiveMaxLength = (validation.maxLength as any).value;
    }
  }

  useEffect(() => {
    if (value !== undefined && value !== null) {
      const valStr = String(value);
      setInputValue(effectiveMaxLength ? valStr.slice(0, effectiveMaxLength) : valStr);
    } else if (inputRef.current?.value) {
      setInputValue(inputRef.current.value);
    }
  });

  const registered = register && name ? register(name, validation) : ({} as any);
  const { ref: registeredRef, ...restRegistered } = registered || {};

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (effectiveMaxLength && val.length > effectiveMaxLength) {
      val = val.slice(0, effectiveMaxLength);
      e.target.value = val;
    }
    setInputValue(val);
    if (restRegistered?.onChange) {
      restRegistered.onChange(e);
    }
    if (onChange) {
      onChange(e);
    }
    if (other?.onChange) {
      other.onChange(e);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    let val = target.value;
    if (effectiveMaxLength && val.length > effectiveMaxLength) {
      val = val.slice(0, effectiveMaxLength);
      target.value = val;
    }
    if (val !== inputValue) {
      setInputValue(val);
    }
    if (other?.onInput) {
      other.onInput(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    let val = e.target.value;
    if (effectiveMaxLength && val.length > effectiveMaxLength) {
      val = val.slice(0, effectiveMaxLength);
      e.target.value = val;
    }
    setInputValue(val);
    if (restRegistered?.onBlur) {
      restRegistered.onBlur(e);
    }
    if (other?.onBlur) {
      other.onBlur(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (other?.onFocus) {
      other.onFocus(e);
    }
  };

  const handleClearField = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setInputValue("");
    if (inputRef.current) {
      inputRef.current.value = "";
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )?.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(inputRef.current, "");
      }
      const event = new Event("input", { bubbles: true });
      inputRef.current.dispatchEvent(event);
      const changeEvent = new Event("change", { bubbles: true });
      inputRef.current.dispatchEvent(changeEvent);
    }

    if (restRegistered?.onChange) {
      restRegistered.onChange({
        target: { name, value: "" },
      } as any);
    }

    if (onChange) {
      onChange({
        target: { name, value: "" },
      } as any);
    }

    if (other?.onChange) {
      other.onChange({
        target: { name, value: "" },
      } as any);
    }
  };

  const isEmail = type === "email" || name?.toLowerCase().includes("email");
  const isPassword = type === "password" || name?.toLowerCase().includes("password");
  const isTargetInput = isEmail || isPassword;

  // Determine validation status icon (ONLY for email & password)
  const getValidationStatus = () => {
    if (!isTargetInput || !inputValue || inputValue.trim() === "") {
      return null;
    }

    if (isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidFormat = emailRegex.test(inputValue.trim());
      return isValidFormat && !error ? "valid" : "invalid";
    }

    if (isPassword) {
      if (error) return "invalid";
      const minLen = validation?.minLength ? Number(validation.minLength) : 6;
      return inputValue.length < minLen ? "invalid" : "valid";
    }

    return null;
  };

  const validationStatus = getValidationStatus();

  const showCharCounter = Boolean(showCounter || effectiveMaxLength !== undefined);
  const hasRightIcons = type === "password" || validationStatus !== null || showCharCounter;

  // Right padding calculation
  const rightPadding = showCharCounter
    ? (suffix ? "!pr-24" : type === "password" && validationStatus !== null ? "!pr-28" : "!pr-20")
    : (type === "password" && validationStatus !== null ? "!pr-16" : hasRightIcons || suffix ? "!pr-10" : "px-3.5");

  // Border & Focus state styles
  const isInvalid = Boolean(error) || validationStatus === "invalid";

  const borderClass = isInvalid
    ? "border-red-500"
    : "border-[#FBD037]";

  const focusRing = isInvalid
    ? "focus:ring-2 focus:ring-red-500/20"
    : "focus:ring-2 focus:ring-[#FBD037]/30";

  const defaultInputClass = `w-full rounded-lg pt-3.5 pb-2.5 px-3.5 outline-none text-xs bg-yellow-50 border ${borderClass} ${focusRing} text-[#1E293B] ${
    type === "number" ? "hide-spinner" : ""
  } disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200`;

  // Left padding calculation
  const leftPadding = prefix ? "!pl-14" : "";

  let computedInputClassName = inputClassName
    ? `${inputClassName} ${borderClass} ${focusRing} outline-none transition-all duration-200 ${leftPadding} ${rightPadding}`
    : `${defaultInputClass} ${leftPadding} ${rightPadding}`;

  // Floating label logic
  const labelText = title || placeholder;
  const domVal = inputRef.current?.value;
  const hasVal =
    (inputValue !== "" && inputValue !== undefined && inputValue !== null) ||
    (value !== "" && value !== undefined && value !== null) ||
    (defaultValue !== "" && defaultValue !== undefined && defaultValue !== null) ||
    (domVal !== "" && domVal !== undefined && domVal !== null);

  const isFloating =
    isFocused ||
    hasVal ||
    type === "date" ||
    type === "time" ||
    type === "datetime-local";

  const inputField = (
    <input
      ref={(e) => {
        inputRef.current = e;
        if (registeredRef) {
          if (typeof registeredRef === "function") registeredRef(e);
          else (registeredRef as any).current = e;
        }
      }}
      type={type === "password" && showPassword ? "text" : type}
      placeholder={placeholder ? (isFloating || isFocused ? t(placeholder) : " ") : " "}
      className={`peer ${computedInputClassName}`}
      defaultValue={defaultValue}
      value={value}
      {...restRegistered}
      {...other}
      {...rest}
      onChange={handleChange}
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      {...(step !== undefined ? { step } : {})}
      {...(inputMode !== undefined ? { inputMode } : {})}
      autoComplete="off"
      onKeyDown={(e) => {
        if (type === "number" && ["e", "E", "+", "-"].includes(e.key)) {
          e.preventDefault();
        }
        if (other?.onKeyDown) {
          other.onKeyDown(e);
        }
      }}
      min={min}
      max={max}
      {...(effectiveMaxLength !== undefined ? { maxLength: effectiveMaxLength } : {})}
    />
  );

  return (
    <div className={`relative pt-1.5 ${className}`}>
      <div className="w-full flex-1 min-w-0">
        {!removeDiv ? (
          <div className="relative flex items-center">
            {prefix && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center z-10 pointer-events-auto">
                {prefix}
              </div>
            )}
            {inputField}

            {/* Animated Floating Label */}
            {labelText && (
              <label
                className={`absolute pointer-events-none transition-all duration-200 ease-in-out select-none rounded-md
                  ${
                    isFloating
                      ? "-top-2.5 left-2.5 translate-y-0 text-[11px] font-semibold bg-yellow-50 z-10 px-1.5"
                      : `${prefix ? "left-14" : "left-2.5"} top-1/2 -translate-y-1/2 text-xs bg-transparent z-0 px-1 peer-focus:-top-2.5 peer-focus:left-2.5 peer-focus:translate-y-0 peer-focus:text-[11px] peer-focus:font-semibold peer-focus:bg-yellow-50 peer-focus:z-10 peer-focus:px-1.5 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-2.5 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:bg-yellow-50 peer-[:not(:placeholder-shown)]:z-10 peer-[:not(:placeholder-shown)]:px-1.5 peer-autofill:-top-2.5 peer-autofill:left-2.5 peer-autofill:translate-y-0 peer-autofill:text-[11px] peer-autofill:font-semibold peer-autofill:bg-yellow-50 peer-autofill:z-10 peer-autofill:px-1.5`
                  }
                  ${
                    isInvalid
                      ? "!text-red-500"
                      : "!text-black font-medium"
                  }
                  ${disabled ? "!bg-gray-100 !text-gray-400" : ""}
                  ${labelClass}
                `}
              >
                {t(labelText)}
                {validation?.required && (
                  <span className="text-red-500 ml-0.5">*</span>
                )}
              </label>
            )}

            {/* Right icons & counter */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10 pointer-events-auto">
              {suffix}
              {showCharCounter && (
                <span className="text-xs text-[#FBD037] font-semibold select-none">
                  {effectiveMaxLength ? `${inputValue.length}/${effectiveMaxLength}` : `${inputValue.length}`}
                </span>
              )}
              {validationStatus === "valid" && (
                <span
                  className="w-5 h-5 rounded-full bg-[#FBD037] text-black flex items-center justify-center shadow-sm transition-all duration-200"
                  title="Valid"
                >
                  <IconCheck size={13} stroke={3} />
                </span>
              )}
              {validationStatus === "invalid" && (
                <button
                  type="button"
                  onClick={handleClearField}
                  className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm hover:bg-red-600 transition-all duration-200 cursor-pointer focus:outline-none"
                  title={t("Clear")}
                  tabIndex={-1}
                >
                  <IconX size={13} stroke={3} />
                </button>
              )}
              {type === "password" && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none flex items-center"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <IconEyeOff size={18} />
                  ) : (
                    <IconEye size={18} />
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          inputField
        )}
        <FormErrorMessage error={error} />
        {type === "password" &&
          !disabled &&
          (showPasswordStrength !== undefined
            ? showPasswordStrength
            : !name?.toLowerCase().includes("current") &&
              !name?.toLowerCase().includes("confirm")) && (
            <PasswordStrengthIndicator
              value={inputValue || (value !== undefined && value !== null ? String(value) : "")}
            />
          )}
      </div>
    </div>
  );
};

export default NewinputField;
